import CareSuperLogo from "@/components/CareSuperLogo";
import JourneyLayout from "@/components/JourneyLayout";
import ULThemeCard from "@/components/ULThemeCard";
import { detectFlow, journeySteps } from "@/utils/flow";
import { applyCareSuperBrand } from "@/utils/theme/careSuperBrand";
import { applyAuth0Theme } from "@/utils/theme/themeEngine";

import EmailIdentifierChallengeForm from "./components/EmailIdentifierChallengeForm";
import Footer from "./components/Footer";
import Header from "./components/Header";
import { useEmailIdentifierChallengeManager } from "./hooks/useEmailIdentifierChallengeManager";

/**
 * The `email-identifier-challenge` screen serves TWO different journeys:
 *
 *   SIGNUP          step 2 of the join wizard ("Confirm your email")
 *   PASSWORD RESET  the code challenge after "Forgot Your Password?"
 *
 * Same prompt, same screen name, same URL (/u/email-identifier/challenge) —
 * the same shared-screen trap already documented on `passkey-enrollment`.
 *
 * This file used to wrap BOTH in JourneyLayout unconditionally, so a member
 * resetting their password was shown the join stepper: "Let's start /
 * Confirm your email / Personal info / Set password / Check details", with
 * step 2 highlighted. Three of those steps never happen in a reset, and
 * "Check details" implies an account is being created. Reported from a live
 * Flow A reset on 2026-07-31.
 *
 * THE DISCRIMINATOR: `screen.data.email`.
 *
 *   signup -> present (the address the member just typed)
 *   reset  -> absent
 *
 * Both halves observed live on this screen, not reasoned about — it is why
 * the reset screen reads "We've sent a 6-digit code to your email" while
 * signup reads "...to name@example.com". Header.tsx already depends on
 * exactly this difference via `data?.email || locales.header.emailFallback`,
 * so the two are consistent by construction rather than independently
 * guessed.
 *
 * ⚠️ WHY NOT `user.id`, the discriminator passkey-enrollment uses. It does not
 * work here, and the reason is worth writing down because reaching for it is
 * the obvious move. That screen's two moments are signup vs an AUTHENTICATED
 * login, so user context is empty in one and populated in the other. Password
 * reset is NOT authenticated — the member has only claimed an email address —
 * so `user.id` is expected to be absent in BOTH of this screen's cases and
 * would silently never fire. Same-shaped problem, different discriminator.
 *
 * FAILURE MODES ARE COSMETIC, deliberately. If `data.email` were ever absent
 * during signup the member loses the progress indicator; if it were ever
 * present during reset they see today's wrong stepper. Neither blocks the
 * journey, which is why a heuristic is acceptable here where it would not be
 * for the passkey abort (that one changes whether a credential is issued).
 */
function EmailIdentifierChallengeScreen() {
  // Extracting attributes from hook made out of EmailIdentifierChallenge instance of Auth0 React ACUL SDK
  const { emailIdentifierChallenge, texts, locales, data } =
    useEmailIdentifierChallengeManager();

  applyAuth0Theme(emailIdentifierChallenge);
  applyCareSuperBrand();
  document.title = texts?.pageTitle || locales?.page?.title;

  const isJoinJourney =
    typeof data?.email === "string" && data.email.length > 0;

  // `showLogo` because the two layouts source the wordmark differently: on the
  // join journey JourneyLayout draws it in the left column beside the stepper,
  // so the card must NOT repeat it. Standalone there is no left column, and
  // the hosted reset screens either side of this one carry the mark inside the
  // widget, so it goes in the card.
  const card = (showLogo: boolean) => (
    <ULThemeCard className="theme-universal w-full gap-0">
      {showLogo ? (
        <CareSuperLogo className="h-10 w-auto self-center mb-6" />
      ) : null}
      <Header />
      <EmailIdentifierChallengeForm />
      <Footer />
    </ULThemeCard>
  );

  // Password reset: no wizard, so no stepper. Same flat white treatment and
  // the same 480px column the join screens use, minus the left stepper
  // column — so it reads as part of the ACUL set rather than a third style.
  //
  // NOT ULThemePageLayout, which was tried and reverted: it applies the
  // theme's `justify-page-layout` widget position, which fights a fixed-width
  // centred column and pushed the whole screen off-centre and oversized. That
  // component suits the hosted widget it was written for; these screens draw
  // their own layout. The flat white is deliberate and matches signup-id /
  // signup-password — the hosted screens either side carry card chrome from
  // the theme, and that difference is pre-existing across the whole ACUL set,
  // not something to fix on this one screen.
  if (!isJoinJourney) {
    return (
      <div className="min-h-screen w-full bg-white flex flex-col items-center px-6 py-12">
        <div className="w-full max-w-[480px]">{card(true)}</div>
      </div>
    );
  }

  return (
    <JourneyLayout
      currentStep={2}
      steps={journeySteps(detectFlow(emailIdentifierChallenge.client?.id))}
    >
      {card(false)}
    </JourneyLayout>
  );
}

export default EmailIdentifierChallengeScreen;
